import React, { FunctionComponent, useState, useEffect } from 'react'
import Modal from '../../modal/Modal'
import ModalClosingButton from '../../modal/ModalClosingButton'
import { Button } from '../../../base-components'
import ComparisonResultData from '../../../entities/ComparisonResultData'
import ComparisonData from '../../../entities/ComparisonData'
import useTranslation from '../../../hooks/useTranslation'
import PdfIcon from '../../../assets/media/icons/pdf.svg'
import filterCategoriesBySelection from '../helpers/filterCategoriesBySelection'
import filterRowsByOnlyDifferent from '../helpers/filterRows'
import getColumnCount from '../helpers/getColumnCount'
import getAvailableCategories from '../helpers/getAvailableCategories'
import ComparisonPdfFilters from '../comparison-pdf-filters/ComparisonPdfFilters'
import ComparisonPdfTable from '../comparison-pdf-table/ComparisonPdfTable'
import { createComparisonPdf as createComparisonPdfAction } from '../../../redux/actions/app/comparisonPdf.actions'
import ComparisonPdfPayload from '../../../entities/ComparisonPdfPayload'
import style from './comparisonPdfModal.css'

export type Props = {
    resultData: ComparisonResultData
    comparisonData: ComparisonData
    createComparisonPdf: typeof createComparisonPdfAction
    requestIsPending: boolean
    isVisible: boolean
    onClose: () => void
}

const ComparisonPdfModal: FunctionComponent<Props> = (props) => {
    const { resultData, comparisonData, createComparisonPdf, requestIsPending, isVisible, onClose } = props
    const { t } = useTranslation()
    const [onlyDifferent, setOnlyDifferent] = useState(comparisonData.onlyDifferent)

    const availableCategories = getAvailableCategories(resultData)

    const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
        new Set(availableCategories.map(cat => cat.id)),
    )

    useEffect(() => {
        const categories = getAvailableCategories(resultData)
        if (categories.length > 0) {
            setSelectedCategories(new Set(categories.map(cat => cat.id)))
        }
    }, [resultData])

    const filteredCategories = filterCategoriesBySelection(resultData, selectedCategories)
    const columnCount = getColumnCount(filteredCategories)

    const handleCategoryToggle = (categoryId: string) => {
        setSelectedCategories(prev => {
            const newSelected = new Set(prev)
            if (newSelected.has(categoryId)) {
                newSelected.delete(categoryId)
            } else {
                newSelected.add(categoryId)
            }
            return newSelected
        })
    }

    const handleGeneratePdf = () => {
        const categoriesToExport = filteredCategories.map(category => ({
            ...category,
            subCategory: category.subCategory.map(subcategory => ({
                ...subcategory,
                rows: filterRowsByOnlyDifferent(subcategory.rows, onlyDifferent),
            })),
        }))

        const pdfPayload: ComparisonPdfPayload = {
            info: resultData?.info,
            categories: categoriesToExport,
        }

        createComparisonPdf(pdfPayload)
    }

    return (
        <Modal isVisible={isVisible} onClose={onClose}>
            <ModalClosingButton onClick={onClose} />
            <section className={style.modalContainer}>
                <ComparisonPdfFilters
                    availableCategories={availableCategories}
                    selectedCategories={selectedCategories}
                    onlyDifferent={onlyDifferent}
                    onCategoryToggle={handleCategoryToggle}
                    onOnlyDifferentChange={setOnlyDifferent}
                />

                <div className={style.resultsSection}>
                    {filteredCategories.map((category, categoryIndex) => (
                        <ComparisonPdfTable
                            key={category.id}
                            category={category}
                            categoryIndex={categoryIndex}
                            columnCount={columnCount}
                            onlyDifferent={onlyDifferent}
                            resultData={resultData}
                        />
                    ))}
                </div>

                <div className={style.modalFooter}>
                    <Button
                        variant="primary"
                        onClick={handleGeneratePdf}
                        disabled={requestIsPending || filteredCategories.length === 0}
                        loading={requestIsPending}
                        className={style.generatePdfButton}
                    >
                        <span className={style.pdfButtonContent}>
                            <PdfIcon className={style.pdfButtonIcon} />
                            <span>{t('compare.generatePdfButtonText')}</span>
                        </span>
                    </Button>
                </div>
            </section>
        </Modal>
    )
}

export default ComparisonPdfModal
